/**
 * Comparison with the previous equivalent period (CMP-01…04): the previous
 * range of a preset or custom period, the "vs. …" label next to a delta, and
 * the delta itself (% or percentage points).
 */
import { DateTime, type DurationLikeObject } from 'luxon';
import type { Locales, TranslationFunctions } from '@/i18n/i18n-types';
import type { PredefinedRange } from '@/hooks/use-metrics-date-filter';
import { getLuxonLocale } from '@/lib/utils';

export type DeltaView =
  | { kind: 'up' | 'down'; text: string }
  | { kind: 'noBase' }
  | { kind: 'none' };

export interface DateRange {
  from: string;
  to: string;
}

/** How far each end of a preset moves back (Assumptions "Período anterior"). */
const PRESET_SHIFT: Record<PredefinedRange, DurationLikeObject> = {
  today: { days: 1 },
  yesterday: { days: 1 },
  week_to_date: { weeks: 1 },
  last_7_days: { days: 7 },
  month_to_date: { months: 1 },
  last_30_days: { days: 30 },
  last_3_months: { months: 3 },
  last_6_months: { months: 6 },
  year_to_date: { years: 1 },
  last_12_months: { months: 12 },
};

const ROLLING_PRESETS: ReadonlySet<PredefinedRange> = new Set([
  'last_7_days',
  'last_30_days',
  'last_3_months',
  'last_6_months',
  'last_12_months',
]);

/**
 * Previous equivalent range, in the browser's zone. `null` for invalid dates.
 */
export function previousRange(
  input: DateRange & { preset?: PredefinedRange }
): DateRange | null {
  const from = DateTime.fromISO(input.from);
  const to = DateTime.fromISO(input.to);
  if (!from.isValid || !to.isValid) return null;

  if (input.preset) {
    const shift = PRESET_SHIFT[input.preset];
    // "Últimos N dias/meses" end right before the current period starts, so
    // the two windows never overlap (decision of 2026-10-27).
    const end = ROLLING_PRESETS.has(input.preset)
      ? from.minus({ milliseconds: 1 })
      : to.minus(shift);
    return { from: from.minus(shift).toISO(), to: end.toISO() };
  }

  // SPEC_DEVIATION: "Personalizado → recua pela duração (to − from)" moves
  // the window back by the duration plus 1 ms, so it ends right before `from`.
  // Reason: a custom range is [00:00:00.000, 23:59:59.999]; moving it back by
  // `to − from` alone would start it at 00:00:00.001 and end it on `from`
  // itself, overlapping the current period by one instant.
  const end = from.minus({ milliseconds: 1 });
  return {
    from: end.minus(to.diff(from)).toISO(),
    to: end.toISO(),
  };
}

/** "vs. …" label shown next to a delta (CMP-03). */
export function comparisonLabel(
  preset: PredefinedRange | undefined,
  LL: TranslationFunctions,
  now: DateTime
): string {
  switch (preset) {
    case 'today':
      return LL.periodComparison.today({ time: now.toFormat('HH:mm') });
    case 'yesterday':
      return LL.periodComparison.yesterday();
    case 'last_7_days':
      return LL.periodComparison.lastDays({ days: 7 });
    case 'last_30_days':
      return LL.periodComparison.lastDays({ days: 30 });
    case 'week_to_date':
      return LL.periodComparison.weekToDate();
    case 'month_to_date':
      return LL.periodComparison.monthToDate();
    case 'year_to_date':
      return LL.periodComparison.yearToDate();
    case 'last_3_months':
      return LL.periodComparison.lastMonths({ months: 3 });
    case 'last_6_months':
      return LL.periodComparison.lastMonths({ months: 6 });
    case 'last_12_months':
      return LL.periodComparison.lastMonths({ months: 12 });
    case undefined:
      return LL.periodComparison.custom();
  }
}

/** Half away from zero at 1 decimal, ignoring float noise (17.65 → 17.7). */
export function roundTo1(value: number): number {
  const magnitude = Math.round(Number((Math.abs(value) * 10).toPrecision(12)));
  return (Math.sign(value) * magnitude) / 10;
}

function signedView(value: number, suffix: string, locale: Locales): DeltaView {
  const rounded = roundTo1(value);
  const digits = new Intl.NumberFormat(getLuxonLocale(locale), {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(Math.abs(rounded));
  // ≥ 0 (as displayed) is up; a value that rounds to 0 never shows "−0,0".
  return rounded >= 0
    ? { kind: 'up', text: `+${digits}${suffix}` }
    : { kind: 'down', text: `−${digits}${suffix}` };
}

/**
 * `current / previous − 1` with 1 decimal (CMP-01). Previous 0 → `noBase`;
 * previous unavailable (failed query) → `none` (CMP-02).
 */
// SPEC_DEVIATION: takes `locale` (design: `computeDelta(current, previous)`).
// Reason: `text` uses the locale's decimal separator ("+17,7%" × "+17.7%").
export function computeDelta(
  current: number,
  previous: number | null | undefined,
  locale: Locales
): DeltaView {
  if (previous === undefined || previous === null) return { kind: 'none' };
  if (previous === 0) return { kind: 'noBase' };
  return signedView((current / previous - 1) * 100, '%', locale);
}

/**
 * Difference in percentage points between two shares in 0–1 (CMP-04).
 * Current share `null` (app + web = 0) or previous unavailable → `none`;
 * previous share `null` (no sales then) → `noBase`.
 */
// SPEC_DEVIATION: takes `locale` and `previousShare` may be `undefined`
// (failed query). Reason: same as `computeDelta`.
export function computePpDelta(
  currentShare: number | null,
  previousShare: number | null | undefined,
  locale: Locales
): DeltaView {
  if (currentShare === null || previousShare === undefined) {
    return { kind: 'none' };
  }
  if (previousShare === null) return { kind: 'noBase' };
  return signedView((currentShare - previousShare) * 100, ' p.p.', locale);
}

/**
 * Previous period's values under the current buckets, by index (ANLT-04), as
 * `previous`: a shorter previous period ends earlier (`null` after its last
 * bucket); a 0 stays 0.
 */
export function alignByIndex<T extends object>(
  rows: T[],
  previousValues: number[]
): Array<T & { previous: number | null }> {
  return rows.map((row, index) => ({
    ...row,
    previous: previousValues[index] ?? null,
  }));
}
